import React, { useState, useEffect } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LineChart,
    Line
} from 'recharts';
import API_CONFIG from '../config/api';
import { GenerationsData, ChartDataItem, GenerationUsageItem } from '../types/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

interface GenerationsProps {
    timeRange: string;
}

const Generations: React.FC<GenerationsProps> = ({ timeRange }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [generationsData, setGenerationsData] = useState<GenerationsData | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [usageTypeFilter, setUsageTypeFilter] = useState<string>('all');

    // State for collapsible sections
    const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({
        charts: false,
        dailyLineChart: false,
        detailedTable: false,
        usageData: false
    });

    // Toggle function for collapsible sections
    const toggleSection = (sectionKey: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Toggle function for row expansion
    const toggleRowExpansion = (index: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    // API Helper Function with request deduplication
    const apiCall = async (endpoint: string) => {
        try {
            const url = `${API_CONFIG.BASE_URL}${endpoint}?period=${timeRange}`;
            const data = await requestManager.makeRequest(url);

            if (!data.success) throw new Error(data.message || 'API call failed');
            return data.data;
        } catch (error) {
            console.error(`API Error for ${endpoint}:`, error);
            throw error;
        }
    };

    const fetchGenerationsData = async () => {
        try {
            setLoading(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.ANALYTICS.GENERATIONS);
            setGenerationsData(data);
        } catch (error) {
            console.error('Error fetching generations data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGenerationsData();
    }, [timeRange]);

    useEffect(() => {
        setUsageTypeFilter('all');
        setExpandedRows(new Set());
    }, [timeRange]);

    // Listen for cache clear events to auto-refresh data
    useEffect(() => {
        const unsubscribe = cacheEventManager.subscribe(() => {
            fetchGenerationsData();
        });

        return unsubscribe;
    }, [timeRange]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading generations data...</p>
                </div>
            </div>
        );
    }

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899'];

    // Prepare data for charts
    const pieData = generationsData ? (() => {
        const total = generationsData.total_generations || 0;

        return [
            { name: 'AI Photoshoot', value: generationsData.ai_photoshoot || 0, percentage: total > 0 ? (((generationsData.ai_photoshoot || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Tryon', value: generationsData.tryon || 0, percentage: total > 0 ? (((generationsData.tryon || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Advance AI Photoshoot', value: generationsData.realistic_tryon || 0, percentage: total > 0 ? (((generationsData.realistic_tryon || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Video Generation', value: generationsData.video_generation || 0, percentage: total > 0 ? (((generationsData.video_generation || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Fashion Extractor', value: generationsData.fashion_extractor || 0, percentage: total > 0 ? (((generationsData.fashion_extractor || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Upscaler', value: generationsData.upscaler || 0, percentage: total > 0 ? (((generationsData.upscaler || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Background Removal', value: generationsData.background_removal || 0, percentage: total > 0 ? (((generationsData.background_removal || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Image Editing', value: generationsData.image_editing || 0, percentage: total > 0 ? (((generationsData.image_editing || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Background Change', value: generationsData.background_change || 0, percentage: total > 0 ? (((generationsData.background_change || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'AI Workflow', value: generationsData.ai_workflow || 0, percentage: total > 0 ? (((generationsData.ai_workflow || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'AI Poses', value: generationsData.ai_poses || 0, percentage: total > 0 ? (((generationsData.ai_poses || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'AI Designer', value: generationsData.ai_designer || 0, percentage: total > 0 ? (((generationsData.ai_designer || 0) / total) * 100).toFixed(1) : '0.0' },
            { name: 'Wrinkle Removal', value: generationsData.ai_wrinkle_removal || 0, percentage: total > 0 ? (((generationsData.ai_wrinkle_removal || 0) / total) * 100).toFixed(1) : '0.0' }

        ].filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);
    })() : [];

    const barData = pieData.map(item => ({
        ...item,
        name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name
    }));

    // Process daily generation data for line chart using table rows (local date) with per-tool counts in payload
    const processDailyData = () => {
        if (!generationsData?.generation_usage_data) return [] as Record<string, number | string>[];

        // Build per-date, per-tool totals using local date keys
        const byDate: { [dateKey: string]: { [tool: string]: number } } = {};

        generationsData.generation_usage_data.forEach(item => {
            const d = new Date(item.timestamp);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const dateKey = `${yyyy}-${mm}-${dd}`; // local date key

            const tool = item.tool_name || 'Unknown';

            if (!byDate[dateKey]) byDate[dateKey] = {};
            byDate[dateKey][tool] = (byDate[dateKey][tool] || 0) + (item.quantity || 0);
        });

        const sortedDates = Object.keys(byDate).sort();

        const dailySeries = sortedDates.map(dateKey => {
            const toolCounts = byDate[dateKey];
            const total = Object.values(toolCounts).reduce((sum, n) => sum + n, 0);
            return {
                date: new Date(dateKey).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                fullDate: dateKey,
                ...toolCounts,
                total
            } as Record<string, number | string>;
        });

        return dailySeries;
    };

    const dailySeries = processDailyData();

    const usageData = generationsData?.generation_usage_data ?? [];
    const toolTypeOptions = Array.from(new Set(usageData.map(item => item.tool_name).filter(Boolean))).sort();
    const filteredUsageData = usageTypeFilter === 'all'
        ? usageData
        : usageData.filter(item => item.tool_name === usageTypeFilter);

    // Debug logging removed

    // Collapsible Header Component
    const CollapsibleHeader: React.FC<{
        title: string;
        count?: number;
        color: string;
        isCollapsed: boolean;
        onToggle: () => void;
    }> = ({ title, count, color, isCollapsed, onToggle }) => (
        <div
            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
            onClick={onToggle}
        >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                <div className={`w-4 h-4 ${color} rounded-full mr-2`}></div>
                {title} {count !== undefined && `(${count})`}
            </h3>
            <div className="flex items-center">
                <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    );

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                {/* Summary Cards - Single Row Layout */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Total Generations</p>
                        <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
                            {generationsData?.total_generations?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Most Popular</p>
                        <p className="text-lg font-bold text-gray-800 dark:text-white mt-1 capitalize">
                            {generationsData?.most_popular_generation?.replace(/_/g, ' ') || 'N/A'}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Generation Types</p>
                        <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
                            {pieData.length}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Top Performer</p>
                        <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
                            {pieData[0]?.value?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {pieData[0]?.name || 'N/A'}
                        </p>
                    </div>
                </div>

                {/* Charts - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Generation Charts"
                        isCollapsed={collapsedSections.charts}
                        onToggle={() => toggleSection('charts')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.charts && (
                        <div className="flex gap-6 mt-6">
                            {/* Pie Chart - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Generation Types Distribution</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, percentage }) => `${name}: ${percentage}%`}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Bar Chart - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Generation Counts by Type</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="name"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), 'Count']}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <Bar dataKey="value">
                                                {barData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Daily Line Chart */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Daily Generation Trends"
                        count={dailySeries.length}
                        isCollapsed={collapsedSections.dailyLineChart}
                        onToggle={() => toggleSection('dailyLineChart')}
                        color="bg-emerald-500"
                    />

                    {!collapsedSections.dailyLineChart && (
                        <div className="mt-6">
                            {dailySeries.length > 0 ? (
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={dailySeries} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 12 }}
                                                angle={-45}
                                                textAnchor="end"
                                                height={60}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                content={({ active, payload, label }) => {
                                                    if (active && payload && payload.length) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="glass-card p-4">
                                                                <p className="font-semibold text-gray-900 dark:text-white mb-2">
                                                                    {label}
                                                                </p>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                                                    Total: {data.total} generations
                                                                </p>
                                                                <div className="space-y-1">
                                                                    {Object.entries(data).filter(([key, value]) =>
                                                                        !['date', 'fullDate', 'total'].includes(key) && (value as number) > 0
                                                                    ).map(([toolName, count]) => (
                                                                        <div key={toolName} className="flex justify-between text-sm">
                                                                            <span className="text-gray-600 dark:text-gray-400">{toolName}:</span>
                                                                            <span className="font-medium text-gray-900 dark:text-white">{count as number}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="total"
                                                stroke="#10B981"
                                                strokeWidth={3}
                                                dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8, stroke: '#10B981', strokeWidth: 2 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-gray-500 dark:text-gray-400">
                                        No daily generation data available for the selected period.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Detailed Table */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Generation Types Breakdown"
                        count={pieData.length}
                        isCollapsed={collapsedSections.detailedTable}
                        onToggle={() => toggleSection('detailedTable')}
                        color="bg-blue-500"
                    />

                    {!collapsedSections.detailedTable && (
                        <div className="overflow-x-auto mt-6">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Generation Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Count
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Percentage
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                    {pieData.map((item, index) => (
                                        <tr key={item.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div
                                                        className="w-4 h-4 rounded-full mr-3"
                                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                                    ></div>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {item.name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {item.value.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {item.percentage}%
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${index === 0
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                                    }`}>
                                                    {index === 0 ? 'Most Popular' : 'Active'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Generation Usage Data Table */}
                <div className="glass-card p-6">
                    <CollapsibleHeader
                        title="Generation Usage Details"
                        count={usageTypeFilter === 'all' ? usageData.length : filteredUsageData.length}
                        isCollapsed={collapsedSections.usageData}
                        onToggle={() => toggleSection('usageData')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.usageData && (
                        <div className="mt-6">
                            {usageData.length > 0 ? (
                                <>
                                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {usageTypeFilter === 'all'
                                                ? `Showing all ${usageData.length} generations`
                                                : `Showing ${filteredUsageData.length} of ${usageData.length} generations`}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <label htmlFor="usage-type-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Filter by type
                                            </label>
                                            <select
                                                id="usage-type-filter"
                                                value={usageTypeFilter}
                                                onChange={(e) => {
                                                    setUsageTypeFilter(e.target.value);
                                                    setExpandedRows(new Set());
                                                }}
                                                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            >
                                                <option value="all">All types</option>
                                                {toolTypeOptions.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type} ({usageData.filter(item => item.tool_name === type).length})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {filteredUsageData.length > 0 ? (
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Tool Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Quantity
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Credits Consumed
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Timestamp
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-transparent">
                                            {filteredUsageData
                                                .slice()
                                                .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
                                                .map((item: GenerationUsageItem, index: number) => (
                                                    <React.Fragment key={index}>
                                                        <tr
                                                            className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-700"
                                                            onClick={() => toggleRowExpansion(index)}
                                                        >
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="mr-2">
                                                                        <svg
                                                                            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${expandedRows.has(index) ? 'rotate-90' : ''}`}
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                        </svg>
                                                                    </div>
                                                                    <div className="flex-shrink-0 h-10 w-10">
                                                                        <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold text-sm">
                                                                            {(item.name || item.email || 'U').charAt(0).toUpperCase()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                            {item.name || 'N/A'}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {item.email}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                                                    {item.tool_name}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {item.quantity}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                    {item.credits_consumed}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                                {item.timestamp ? new Date(item.timestamp).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                }) : 'N/A'}
                                                            </td>
                                                        </tr>
                                                        {expandedRows.has(index) && (
                                                            <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                                                                <td colSpan={5} className="px-6 py-4">
                                                                    <div className="space-y-4">
                                                                        {/* Input and Output Images/Videos Side by Side */}
                                                                        {((item.input_image_urls && item.input_image_urls.length > 0) ||
                                                                            (item.output_image_urls && item.output_image_urls.length > 0) ||
                                                                            (item.output_video_urls && item.output_video_urls.length > 0)) && (
                                                                                <div className="flex gap-4">
                                                                                    {/* Input Images */}
                                                                                    {item.input_image_urls && item.input_image_urls.length > 0 && (
                                                                                        <div className='ml-10'>
                                                                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                                                                Input Images
                                                                                            </h4>
                                                                                            <div className="flex flex-wrap gap-4">
                                                                                                {item.input_image_urls.map((url, imgIndex) => (
                                                                                                    <div key={imgIndex} className="relative">
                                                                                                        <img
                                                                                                            src={url}
                                                                                                            alt={`Input ${imgIndex + 1}`}
                                                                                                            className="w-48 h-48 object-cover rounded-lg border-2 border-gray-300 dark:border-gray-600"
                                                                                                            onError={(e) => {
                                                                                                                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="monospace" font-size="16" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E';
                                                                                                            }}
                                                                                                        />
                                                                                                        <a
                                                                                                            href={url}
                                                                                                            target="_blank"
                                                                                                            rel="noopener noreferrer"
                                                                                                            className="absolute top-2 right-2 bg-white dark:bg-slate-900 p-1 rounded-full shadow-lg hover:bg-gray-100"
                                                                                                        >
                                                                                                            <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                                            </svg>
                                                                                                        </a>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Output Images or Videos */}
                                                                                    {((item.output_image_urls && item.output_image_urls.length > 0) ||
                                                                                        (item.output_video_urls && item.output_video_urls.length > 0)) && (
                                                                                            <div className='ml-10'>
                                                                                                {/* Output Images */}
                                                                                                {item.output_image_urls && item.output_image_urls.length > 0 && (
                                                                                                    <>
                                                                                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                                                                            Output Images
                                                                                                        </h4>
                                                                                                        <div className="flex flex-wrap gap-4">
                                                                                                            {item.output_image_urls.map((url, imgIndex) => (
                                                                                                                <div key={imgIndex} className="relative">
                                                                                                                    <img
                                                                                                                        src={url}
                                                                                                                        alt={`Output ${imgIndex + 1}`}
                                                                                                                        className="w-48 h-48 object-cover rounded-lg border-2 border-green-300 dark:border-green-600"
                                                                                                                        onError={(e) => {
                                                                                                                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="monospace" font-size="16" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E';
                                                                                                                        }}
                                                                                                                    />
                                                                                                                    <a
                                                                                                                        href={url}
                                                                                                                        target="_blank"
                                                                                                                        rel="noopener noreferrer"
                                                                                                                        className="absolute top-2 right-2 bg-white dark:bg-slate-900 p-1 rounded-full shadow-lg hover:bg-gray-100"
                                                                                                                    >
                                                                                                                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                                                        </svg>
                                                                                                                    </a>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    </>
                                                                                                )}

                                                                                                {/* Output Videos */}
                                                                                                {item.output_video_urls && item.output_video_urls.length > 0 && (
                                                                                                    <>
                                                                                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                                                                            Output Videos
                                                                                                        </h4>
                                                                                                        <div className="flex flex-wrap gap-4">
                                                                                                            {item.output_video_urls.map((url, vidIndex) => (
                                                                                                                <div key={vidIndex} className="relative">
                                                                                                                    <video
                                                                                                                        controls
                                                                                                                        className="w-48 h-48 rounded-lg border-2 border-green-300 dark:border-green-600"
                                                                                                                    >
                                                                                                                        <source src={url} type="video/mp4" />
                                                                                                                        Your browser does not support the video tag.
                                                                                                                    </video>
                                                                                                                    <a
                                                                                                                        href={url}
                                                                                                                        target="_blank"
                                                                                                                        rel="noopener noreferrer"
                                                                                                                        className="absolute top-2 right-2 bg-white dark:bg-slate-900 p-1 rounded-full shadow-lg hover:bg-gray-100"
                                                                                                                    >
                                                                                                                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                                                        </svg>
                                                                                                                    </a>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    </>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            )}

                                                                        {/* No media message */}
                                                                        {(!item.input_image_urls || item.input_image_urls.length === 0) &&
                                                                            (!item.output_image_urls || item.output_image_urls.length === 0) &&
                                                                            (!item.output_video_urls || item.output_video_urls.length === 0) && (
                                                                                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                                                                    No input/output media available for this generation.
                                                                                </div>
                                                                            )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <div className="text-gray-500 dark:text-gray-400">
                                                No generations match the selected type filter.
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-gray-500 dark:text-gray-400">
                                        No generation usage data available for the selected period.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Generations;
